import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/auth-context";
import { useViewingOrg } from "@/contexts/viewing-org-context";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { customInstance } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

interface Industry {
  id: number;
  name: string;
}

interface EquipmentType {
  id: number;
  name: string;
  slug: string;
  display_order: number;
}

interface PrimaryEquipFieldRow {
  key?: string;
  label: string;
}

interface Organization {
  id: number;
  name: string;
  slug: string;
  organization_type?: string;
  allowed_industries: number[];
  allowed_equipment_types: number[];
  primary_equipment_custom_field_defs?: { key: string; label: string }[];
  logo_url?: string | null;
  show_prequals_after_submit?: boolean;
  /** When false, hide simple interest % and APR on thank-you / dealer offer helper (payment unchanged). */
  show_prequal_interest_and_apr?: boolean;
  send_upload_link_email?: boolean;
  website_url?: string | null;
  abandon_notification_email?: string | null;
  embed_enabled?: boolean;
  embed_allowed_domains?: string[];
  embed_script_url?: string | null;
  /** Full copy/paste HTML from API (matches Django admin); preferred over client-built v2 snippet. */
  embed_snippet_html?: string | null;
  /** CMP-safe snippet for cookie-consent hosts; null for marketplace partners. */
  embed_snippet_cmp_html?: string | null;
  primary_color?: string | null;
  contact_email?: string;
  contact_phone?: string | null;
  financing_page_vision_enabled?: boolean;
  signup_referred_by_name?: string | null;
  signup_referred_by_email?: string | null;
  signup_utm_source?: string | null;
  signup_utm_medium?: string | null;
  signup_utm_campaign?: string | null;
}

interface OrgUser {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  is_active: boolean;
}

export function OrgSettingsPage() {
  const { user } = useAuth();
  const { viewingOrgId } = useViewingOrg();
  const queryClient = useQueryClient();
  const effectiveOrgId = viewingOrgId ?? user?.organization ?? null;

  const canEdit = user?.role === "ORG_ADMIN" || user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER";
  const canSeeSignupAttribution =
    user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER";
  const canEditRatesDisclosure =
    user?.role === "SUPERADMIN" || user?.role === "DD_ACCOUNT_MANAGER";
  const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>([]);
  const [selectedEquipmentTypeIds, setSelectedEquipmentTypeIds] = useState<number[]>([]);
  const [showPrequalsAfterSubmit, setShowPrequalsAfterSubmit] = useState<boolean>(true);
  const [showPrequalInterestAndApr, setShowPrequalInterestAndApr] = useState<boolean>(true);
  const [sendUploadLinkEmail, setSendUploadLinkEmail] = useState<boolean>(true);
  const [websiteUrl, setWebsiteUrl] = useState<string>("");
  const [abandonNotificationEmail, setAbandonNotificationEmail] = useState<string>("");
  const [embedEnabled, setEmbedEnabled] = useState<boolean>(false);
  const [embedDomainsText, setEmbedDomainsText] = useState<string>("");
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [primaryEquipFieldRows, setPrimaryEquipFieldRows] = useState<PrimaryEquipFieldRow[]>([
    { label: "" },
    { label: "" },
    { label: "" },
  ]);
  const [finBrandPrimary, setFinBrandPrimary] = useState("#1a56db");
  const [finBrandAccent, setFinBrandAccent] = useState("#e8effc");
  const [finCtaBg, setFinCtaBg] = useState("");
  const [finCtaText, setFinCtaText] = useState("");
  const [finContactName, setFinContactName] = useState("");
  const [finContactPhone, setFinContactPhone] = useState("");
  const [finContactEmail, setFinContactEmail] = useState("");
  const [finContactAddress, setFinContactAddress] = useState("");
  const [generatedFinancingHtml, setGeneratedFinancingHtml] = useState<string | null>(null);
  const [finColorTab, setFinColorTab] = useState<"colors" | "screenshots">("colors");
  const [screenshotFiles, setScreenshotFiles] = useState<File[]>([]);

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ["organization", effectiveOrgId],
    queryFn: async () => {
      const { data } = await customInstance.get<Organization>(`/api/organizations/${effectiveOrgId}/`);
      return data;
    },
    enabled: !!effectiveOrgId && !!canEdit,
  });

  const { data: salesRepsResponse } = useQuery({
    queryKey: ["org-users", effectiveOrgId, "SALES"],
    queryFn: async () => {
      const { data } = await customInstance.get<{ results: OrgUser[] }>(
        "/api/users/",
        { params: { role: "SALES", organization_id: effectiveOrgId } }
      );
      return data;
    },
    enabled: !!effectiveOrgId && canEdit,
  });

  const salesReps: OrgUser[] = salesRepsResponse?.results ?? [];

  const inviteSalesRep = useMutation({
    mutationFn: async (payload: { email: string; first_name: string; last_name: string }) => {
      const { data } = await customInstance.post<OrgUser>(
        `/api/organizations/${effectiveOrgId}/invite_sales_rep/`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-users", effectiveOrgId, "SALES"] });
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      toast.success("Invite sent. The sales rep will receive an email to set their password.");
    },
    onError: (err: any) => {
      const code = err?.response?.data?.code;
      if (code === "inactive_sales_rep_exists") {
        toast.error("This sales rep already exists but is inactive. Reactivate them from the list below.");
        return;
      }
      const msg = err?.response?.data?.email?.[0] ?? err?.response?.data?.first_name?.[0] ?? err?.response?.data?.last_name?.[0] ?? err?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg ?? "Failed to send invite.");
    },
  });

  const deactivateSalesRep = useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await customInstance.post<OrgUser>(
        `/api/organizations/${effectiveOrgId}/deactivate_sales_rep/`,
        { user_id: userId }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-users", effectiveOrgId, "SALES"] });
      toast.success("Sales rep deactivated.");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? "Failed to deactivate sales rep.");
    },
  });

  const reactivateSalesRep = useMutation({
    mutationFn: async (userId: number) => {
      const { data } = await customInstance.post<OrgUser>(
        `/api/organizations/${effectiveOrgId}/reactivate_sales_rep/`,
        { user_id: userId }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["org-users", effectiveOrgId, "SALES"] });
      toast.success("Sales rep reactivated.");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? "Failed to reactivate sales rep.");
    },
  });

  const { data: intakeData } = useQuery({
    queryKey: ["intake-data-org-settings"],
    queryFn: async () => {
      const { data } = await customInstance.get<{
        industries: Industry[];
        equipment_types: EquipmentType[];
      }>("/api/intake/data/");
      return data;
    },
    enabled: canEdit,
  });

  useEffect(() => {
    if (org) {
      setSelectedIndustryIds(org.allowed_industries ?? []);
      setSelectedEquipmentTypeIds(org.allowed_equipment_types ?? []);
      setShowPrequalsAfterSubmit(org.show_prequals_after_submit ?? false);
      setShowPrequalInterestAndApr(org.show_prequal_interest_and_apr !== false);
      setSendUploadLinkEmail(org.send_upload_link_email ?? false);
      setWebsiteUrl(org.website_url ?? "");
      setAbandonNotificationEmail(org.abandon_notification_email ?? "");
      setEmbedEnabled(!!org.embed_enabled);
      setEmbedDomainsText((org.embed_allowed_domains ?? []).join("\n"));
      const defs = org.primary_equipment_custom_field_defs ?? [];
      const rows: PrimaryEquipFieldRow[] = [0, 1, 2].map((i) =>
        defs[i] ? { key: defs[i].key, label: defs[i].label } : { label: "" }
      );
      setPrimaryEquipFieldRows(rows);
      setFinBrandPrimary(org.primary_color?.trim() || "#1a56db");
      setFinBrandAccent("#e8effc");
      setFinCtaBg("");
      setFinCtaText("");
      setFinContactName(org.name ?? "");
      setFinContactPhone(org.contact_phone ?? "");
      setFinContactEmail(org.contact_email ?? "");
      setFinContactAddress("");
      setGeneratedFinancingHtml(null);
      setFinColorTab("colors");
      setScreenshotFiles([]);
    }
  }, [org]);

  useEffect(() => {
    if (org && !org.financing_page_vision_enabled && finColorTab === "screenshots") {
      setFinColorTab("colors");
    }
  }, [org?.financing_page_vision_enabled, finColorTab, org]);

  const updateOrg = useMutation({
    mutationFn: async (payload: {
      allowed_industries: number[];
      allowed_equipment_types: number[];
      show_prequals_after_submit?: boolean;
      show_prequal_interest_and_apr?: boolean;
      send_upload_link_email?: boolean;
      website_url?: string | null;
      abandon_notification_email?: string | null;
      embed_enabled?: boolean;
      embed_allowed_domains?: string[];
      primary_equipment_custom_field_defs?: { key?: string; label: string }[];
    }) => {
      const { data } = await customInstance.patch<Organization>(
        `/api/organizations/${effectiveOrgId}/`,
        payload
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", effectiveOrgId] });
      toast.success("Organization settings saved.");
    },
    onError: (err: any) => {
      const d = err?.response?.data;
      const msg =
        d?.embed_allowed_domains?.[0] ??
        d?.embed_enabled?.[0] ??
        d?.non_field_errors?.[0] ??
        d?.allowed_industries ??
        d?.allowed_equipment_types ??
        err?.message;
      toast.error(Array.isArray(msg) ? msg[0] : msg ?? "Failed to save.");
    },
  });

  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append("logo", file);
      const { data } = await customInstance.post<Organization>(
        `/api/organizations/${effectiveOrgId}/upload_logo/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organization", effectiveOrgId] });
      setLogoFile(null);
      toast.success("Logo uploaded. It will appear on applications for this organization.");
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error ?? err?.message;
      toast.error(typeof msg === "string" ? msg : "Failed to upload logo.");
    },
  });

  const industries = intakeData?.industries ?? [];
  const equipmentTypes = intakeData?.equipment_types ?? [];

  const handleIndustryToggle = (id: number) => {
    setSelectedIndustryIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleEquipmentTypeToggle = (id: number) => {
    setSelectedEquipmentTypeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const parseEmbedDomains = (): string[] =>
    embedDomainsText
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);

  const handleSave = () => {
    if (effectiveOrgId == null) return;
    if (embedEnabled && parseEmbedDomains().length === 0) {
      toast.error("Add at least one allowed domain when the website embed is enabled (one hostname per line, e.g. example.com).");
      return;
    }
    const primary_equipment_custom_field_defs = primaryEquipFieldRows
      .filter((r) => r.label.trim())
      .map((r) =>
        r.key ? { key: r.key, label: r.label.trim() } : { label: r.label.trim() }
      );
    updateOrg.mutate({
      allowed_industries: selectedIndustryIds,
      allowed_equipment_types: selectedEquipmentTypeIds,
      show_prequals_after_submit: showPrequalsAfterSubmit,
      ...(canEditRatesDisclosure ? { show_prequal_interest_and_apr: showPrequalInterestAndApr } : {}),
      send_upload_link_email: sendUploadLinkEmail,
      website_url: websiteUrl.trim() || null,
      abandon_notification_email: abandonNotificationEmail.trim() || null,
      embed_enabled: embedEnabled,
      embed_allowed_domains: parseEmbedDomains(),
      primary_equipment_custom_field_defs,
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied.`);
    } catch {
      toast.error("Could not copy to clipboard.");
    }
  };

  const applyFinancingPalette = (palette: Record<string, string>) => {
    if (palette.brand_primary) setFinBrandPrimary(palette.brand_primary);
    if (palette.brand_accent) setFinBrandAccent(palette.brand_accent);
    if (palette.cta_bg) setFinCtaBg(palette.cta_bg);
    if (palette.cta_text) setFinCtaText(palette.cta_text);
  };

  const pollExtractFinancingPalette = async (taskId: string) => {
    const maxAttempts = 30;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (attempt > 0) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
      const { data } = await customInstance.get<{
        status: string;
        palette?: Record<string, string>;
        error?: string;
      }>(`/api/organizations/${effectiveOrgId}/extract_financing_palette_status/`, {
        params: { task_id: taskId },
      });
      if (data.status === "pending") {
        continue;
      }
      return data;
    }
    throw new Error("Timed out waiting for color extraction.");
  };

  const extractFinancingPalette = useMutation({
    mutationFn: async (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("screenshots", file));
      const { data } = await customInstance.post<{ task_id: string }>(
        `/api/organizations/${effectiveOrgId}/extract_financing_palette/`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      return data.task_id;
    },
    onSuccess: async (taskId) => {
      try {
        const result = await pollExtractFinancingPalette(taskId);
        if (result.status === "completed" && result.palette) {
          applyFinancingPalette(result.palette);
          setFinColorTab("colors");
          toast.success("Colors extracted. Review the palette below, then click Generate HTML.");
        } else {
          toast.error(
            result.error ?? "Could not extract colors from screenshots. Set colors manually on the From colors tab."
          );
        }
      } catch (err: unknown) {
        const ax = err as { response?: { data?: { error?: string } }; message?: string };
        const msg =
          ax?.response?.data?.error ??
          ax?.message ??
          "Timed out waiting for color extraction. Set colors manually on the From colors tab.";
        toast.error(msg);
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? "Failed to start color extraction.");
    },
  });

  const generateFinancingPage = useMutation({
    mutationFn: async () => {
      const payload: Record<string, string> = {
        brand_primary: finBrandPrimary.trim(),
        brand_accent: finBrandAccent.trim(),
        contact_name: finContactName.trim(),
        contact_phone: finContactPhone.trim(),
        contact_email: finContactEmail.trim(),
        contact_address: finContactAddress.trim(),
      };
      if (finCtaBg.trim()) payload.cta_bg = finCtaBg.trim();
      if (finCtaText.trim()) payload.cta_text = finCtaText.trim();
      const { data } = await customInstance.post<{ html: string; palette: Record<string, string> }>(
        `/api/organizations/${effectiveOrgId}/generate_financing_page/`,
        payload
      );
      return data;
    },
    onSuccess: (data) => {
      setGeneratedFinancingHtml(data.html);
      if (data.palette) applyFinancingPalette(data.palette);
      toast.success("Financing page generated.");
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error ?? "Failed to generate financing page.");
    },
  });

  const downloadFinancingHtml = () => {
    if (!generatedFinancingHtml || !org?.slug) return;
    const blob = new Blob([generatedFinancingHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `financing-${org.slug}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("HTML file downloaded.");
  };

  if (!canEdit) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <p className="text-gray-600">You do not have permission to edit organization settings.</p>
      </div>
    );
  }

  if (!effectiveOrgId) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <p className="text-gray-600">Select an organization (using the org switcher) to edit its settings.</p>
      </div>
    );
  }

  if (orgLoading || !org) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <p className="text-gray-600">Loading…</p>
      </div>
    );
  }

  const isMarketplacePartner = org.organization_type === "MARKETPLACE_PARTNER";
  const signupAttributionRows = [
    org.signup_referred_by_name || org.signup_referred_by_email
      ? {
          label: "Referred by",
          value: [org.signup_referred_by_name, org.signup_referred_by_email].filter(Boolean).join(" — "),
        }
      : null,
    org.signup_utm_source ? { label: "Source", value: org.signup_utm_source } : null,
    org.signup_utm_medium ? { label: "Medium", value: org.signup_utm_medium } : null,
    org.signup_utm_campaign ? { label: "Campaign", value: org.signup_utm_campaign } : null,
  ].filter((row): row is { label: string; value: string } => row != null);
  const showSignupAttribution = canSeeSignupAttribution && signupAttributionRows.length > 0;

  const embedSnippetHtml =
    (org.embed_snippet_html && org.embed_snippet_html.trim()) ||
    (org.embed_script_url && org.slug
      ? `<script src="${org.embed_script_url}" defer></script>\n<button type="button" data-dealerdog-finance data-dealerdog-org="${org.slug}">Get Financing</button>`
      : "");
  const embedSnippetCmpHtml = (org.embed_snippet_cmp_html && org.embed_snippet_cmp_html.trim()) || "";

  const embedWebTeamInstructionsFull = isMarketplacePartner
    ? `1. Save the embed settings above (allowed domains + Enable).

2. Copy the "Embed code" block into your listing template (or layout that wraps each listing).

3. Replace {{ listing... }} placeholders with your platform's template variables (Liquid, Handlebars, JSX, etc. — syntax varies).

4. Keep data-dealerdog-finance, data-dealerdog-org, and data-dealerdog-source-platform (set to "${org.slug}") on the button.

5. The default label is "Get Preapproved"; you may restyle the button to match your marketplace.

6. Local testing: add "localhost" and "127.0.0.1" as allowed domains if you test from your computer.

7. If the modal shows an error, confirm your production hostname is allowlisted (no https://). For debugging, set window.DEALERDOG_DEBUG = true in the console before clicking.`
    : `1. Save the embed settings above (allowed domains + Enable).

2. Choose a snippet:
   - Standard embed code — most dealer sites (script tag). Paste into the footer or a Custom HTML widget.
   - CMP-safe snippet — sites with a cookie consent tool (Osano, etc.) that blocks third-party scripts. Use this instead of the standard snippet on those sites.

3. Keep data-dealerdog-finance and data-dealerdog-org="${org.slug}" on the button or link. You may change the visible label ("Get Financing").

4. Local testing: add "localhost" and "127.0.0.1" as allowed domains if you test from your computer.

5. Optional prefill: add data-dealerdog-equipment-name and/or data-dealerdog-equipment-price on the same button/link.

6. If the modal shows an error, check that your live domain is listed exactly (no https://). For support, enable browser console and set window.DEALERDOG_DEBUG = true before clicking, then share any errors.`;

  const embedShortInstructionsEmail = isMarketplacePartner
    ? `1. Save embed settings in DealerDog (Organization settings → Website financing button).

2. Copy the embed code from DealerDog into your listing template.

3. Replace {{ listing... }} placeholders with your platform's variables. Keep data-dealerdog-org and data-dealerdog-source-platform="${org.slug}".

4. Allowed domains must include your production hostname (e.g. mymarketplace.com covers www and subdomains).

5. For issues, contact your DealerDog account manager.`
    : `1. Save embed settings in DealerDog (Organization settings → Website financing button).

2. Copy either the standard embed code (most sites) or the CMP-safe snippet (cookie consent) from DealerDog and paste into the site footer or Custom HTML area.

3. Keep data-dealerdog-org="${org.slug}" on the button or link. You may change the visible label.

4. Allowed domains must include your production hostname (e.g. mydealer.com covers www and subdomains).

5. For issues, contact your DealerDog account manager.`;

  const handleLogoSubmit = () => {
    if (logoFile && effectiveOrgId != null) uploadLogo.mutate(logoFile);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Organization Settings</h1>
        <p className="mt-1 text-sm text-gray-600">{org.name}</p>
      </div>

      {showSignupAttribution && (
        <Card>
          <CardHeader>
            <CardTitle>How they signed up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {signupAttributionRows.map((row) => (
              <p key={row.label} className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">{row.label}:</span> {row.value}
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Branding</CardTitle>
          <p className="text-sm font-normal text-gray-600">
            Logo shown at the top of the finance application when applicants use this organization&apos;s link.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {org.logo_url ? (
            <div className="flex items-center gap-4">
              <img
                src={org.logo_url}
                alt="Organization logo"
                className="max-h-48 max-w-lg w-auto object-contain border border-gray-200 rounded"
              />
              <span className="text-sm text-gray-500">Current logo</span>
            </div>
          ) : (
            <p className="text-sm text-gray-500">No logo uploaded. DealerDog logo is shown on applications.</p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
              className="text-sm"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleLogoSubmit}
              disabled={!logoFile || uploadLogo.isPending}
            >
              {uploadLogo.isPending ? "Uploading…" : "Upload logo"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Website financing button (embed)</CardTitle>
          <p className="text-sm font-normal text-gray-600">
            {isMarketplacePartner ? (
              <>
                Add a &quot;Get Preapproved&quot; button on each equipment listing that opens the application in a secure modal (v3 embed with listing and source fields).
                Only the domains you list below can load the embed (include your live site;{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">example.com</code> covers{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">www.example.com</code> and subdomains).
              </>
            ) : (
              <>
                Add a &quot;Get Financing&quot; button on your equipment website that opens the application in a secure modal.
                Only the domains you list below can load the embed (include your live site;{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">example.com</code> covers{" "}
                <code className="text-xs bg-gray-100 px-1 rounded">www.example.com</code> and subdomains).
              </>
            )}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={embedEnabled}
              onChange={(e) => setEmbedEnabled(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">
              {isMarketplacePartner ? "Enable website embed (Get Preapproved button)" : "Enable website embed (Get Financing button)"}
            </span>
          </label>
          <div>
            <Label className="text-sm font-medium">Allowed domains (one per line)</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-1">
              Enter the hostname only (no https://). Example: <code className="bg-gray-100 px-1 rounded">mydealer.com</code>
            </p>
            <textarea
              value={embedDomainsText}
              onChange={(e) => setEmbedDomainsText(e.target.value)}
              placeholder={"mydealer.com\nwww.mydealer.com"}
              rows={4}
              className="w-full max-w-lg rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
          <Button onClick={handleSave} disabled={updateOrg.isPending}>
            {updateOrg.isPending ? "Saving…" : "Save embed settings"}
          </Button>

          {embedSnippetHtml && org?.slug && (
            <div className="space-y-3 pt-2 border-t border-gray-200">
              <div>
                <Label className="text-sm font-medium">Standard embed code</Label>
                <p className="text-xs text-gray-500 mt-0.5 mb-2">
                  Paste before the closing <code className="bg-gray-100 px-1 rounded">&lt;/body&gt;</code> tag, or in your theme&apos;s &quot;Custom HTML&quot; / site-wide scripts. Then style the button to match your site.
                </p>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto max-w-3xl whitespace-pre-wrap break-all">
                  {embedSnippetHtml}
                </pre>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={() => copyToClipboard(embedSnippetHtml, "Embed code")}
                >
                  Copy standard embed code
                </Button>
              </div>
              {embedSnippetCmpHtml && (
                <div>
                  <Label className="text-sm font-medium">CMP-safe snippet (cookie consent)</Label>
                  <p className="text-xs text-gray-500 mt-0.5 mb-2">
                    Use this instead of the standard snippet on sites with a cookie consent tool (Osano or similar) that blocks third-party scripts. Includes a plain link to your apply page: if the visitor denies cookies (or the modal API is blocked), the link still opens apply. Note: while the consent banner is open and unanswered, the consent overlay usually blocks clicks on the page — that is expected until they Accept or Deny.
                  </p>
                  <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto max-w-3xl whitespace-pre-wrap break-all">
                    {embedSnippetCmpHtml}
                  </pre>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() => copyToClipboard(embedSnippetCmpHtml, "CMP-safe snippet")}
                  >
                    Copy CMP-safe snippet
                  </Button>
                </div>
              )}
              <div>
                <Label className="text-sm font-medium">Instructions for your web team (copy/paste)</Label>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto max-w-3xl whitespace-pre-wrap mt-2">
                  {embedWebTeamInstructionsFull}
                </pre>
                <div className="flex flex-wrap gap-2 mt-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(embedWebTeamInstructionsFull, "Full instructions")}
                  >
                    Copy full instructions
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(embedShortInstructionsEmail, "Short instructions")}
                  >
                    Copy short instructions (email)
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!isMarketplacePartner && (
        <Card>
          <CardHeader>
            <CardTitle>Generate financing page</CardTitle>
            <p className="text-sm font-normal text-gray-600">
              Build a single HTML file to paste into your website (GoDaddy, Wix custom HTML, etc.). DealerDog
              does not host this page. The Get Financing control uses the CMP-safe embed (works with cookie
              consent tools) and your org slug. Enable the website embed above and allowlist your domain before going live.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
              <button
                type="button"
                className={`px-3 py-1.5 text-sm rounded-t border-b-2 -mb-[9px] ${
                  finColorTab === "colors"
                    ? "border-blue-600 text-blue-700 font-medium"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => setFinColorTab("colors")}
              >
                From colors
              </button>
              <button
                type="button"
                disabled={!org.financing_page_vision_enabled}
                title={
                  org.financing_page_vision_enabled
                    ? undefined
                    : "Screenshot color extraction is not configured on this server."
                }
                className={`px-3 py-1.5 text-sm rounded-t border-b-2 -mb-[9px] disabled:opacity-50 disabled:cursor-not-allowed ${
                  finColorTab === "screenshots"
                    ? "border-blue-600 text-blue-700 font-medium"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
                onClick={() => org.financing_page_vision_enabled && setFinColorTab("screenshots")}
              >
                From screenshots
              </button>
            </div>

            {finColorTab === "colors" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
              <div>
                <Label className="text-sm font-medium">Primary color</Label>
                <div className="mt-1 flex gap-2 items-center">
                  <Input
                    value={finBrandPrimary}
                    onChange={(e) => setFinBrandPrimary(e.target.value)}
                    placeholder="#1a56db"
                    className="font-mono text-sm"
                  />
                  <input
                    type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(finBrandPrimary) ? finBrandPrimary : "#1a56db"}
                    onChange={(e) => setFinBrandPrimary(e.target.value)}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded border border-gray-300"
                    aria-label="Primary color picker"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Accent color</Label>
                <div className="mt-1 flex gap-2 items-center">
                  <Input
                    value={finBrandAccent}
                    onChange={(e) => setFinBrandAccent(e.target.value)}
                    placeholder="#e8effc"
                    className="font-mono text-sm"
                  />
                  <input
                    type="color"
                    value={/^#[0-9A-Fa-f]{6}$/.test(finBrandAccent) ? finBrandAccent : "#e8effc"}
                    onChange={(e) => setFinBrandAccent(e.target.value)}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded border border-gray-300"
                    aria-label="Accent color picker"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium">Button background (optional)</Label>
                <Input
                  value={finCtaBg}
                  onChange={(e) => setFinCtaBg(e.target.value)}
                  placeholder="Defaults to primary"
                  className="mt-1 font-mono text-sm"
                />
              </div>
              <div>
                <Label className="text-sm font-medium">Button text (optional)</Label>
                <Input
                  value={finCtaText}
                  onChange={(e) => setFinCtaText(e.target.value)}
                  placeholder="Defaults to white"
                  className="mt-1 font-mono text-sm"
                />
              </div>
            </div>
            )}

            {finColorTab === "screenshots" && org.financing_page_vision_enabled && (
              <div className="space-y-3 max-w-lg">
                <p className="text-sm text-gray-600">
                  Upload up to 3 screenshots of the dealer website (PNG, JPEG, or WebP). We&apos;ll suggest a color
                  palette for the financing page — you can edit it on the From colors tab before generating HTML.
                </p>
                <div>
                  <Label className="text-sm font-medium">Website screenshots</Label>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    multiple
                    className="mt-1"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files ?? []).slice(0, 3);
                      setScreenshotFiles(picked);
                    }}
                  />
                  {screenshotFiles.length > 0 && (
                    <ul className="mt-2 text-xs text-gray-600 space-y-1">
                      {screenshotFiles.map((file) => (
                        <li key={`${file.name}-${file.size}`}>{file.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    screenshotFiles.length === 0 ||
                    extractFinancingPalette.isPending ||
                    effectiveOrgId == null
                  }
                  onClick={() => extractFinancingPalette.mutate(screenshotFiles)}
                >
                  {extractFinancingPalette.isPending ? "Analyzing screenshots…" : "Extract colors"}
                </Button>
              </div>
            )}

            <div className="space-y-3 max-w-lg border-t border-gray-200 pt-4">
              <Label className="text-sm font-medium">Contact info on the page</Label>
              <Input
                value={finContactName}
                onChange={(e) => setFinContactName(e.target.value)}
                placeholder="Business or contact name"
              />
              <Input
                value={finContactPhone}
                onChange={(e) => setFinContactPhone(e.target.value)}
                placeholder="Phone"
              />
              <Input
                type="email"
                value={finContactEmail}
                onChange={(e) => setFinContactEmail(e.target.value)}
                placeholder="Email"
              />
              <Input
                value={finContactAddress}
                onChange={(e) => setFinContactAddress(e.target.value)}
                placeholder="Street address, city, state"
              />
              <p className="text-xs text-gray-500">
                Empty fields are omitted from the generated page. Phone and email default from org settings when left
                unchanged here.
              </p>
            </div>

            <Button
              type="button"
              onClick={() => generateFinancingPage.mutate()}
              disabled={generateFinancingPage.isPending || effectiveOrgId == null}
            >
              {generateFinancingPage.isPending ? "Generating…" : "Generate HTML"}
            </Button>

            {generatedFinancingHtml && (
              <div className="space-y-3 pt-2 border-t border-gray-200">
                <Label className="text-sm font-medium">Generated HTML</Label>
                <p className="text-xs text-gray-500">
                  Paste this into a custom HTML block on your site. The only external dependency is the DealerDog embed
                  script.
                </p>
                <pre className="text-xs bg-gray-50 border rounded p-3 overflow-x-auto max-w-3xl max-h-64 whitespace-pre-wrap break-all">
                  {generatedFinancingHtml.length > 4000
                    ? `${generatedFinancingHtml.slice(0, 4000)}\n… (truncated preview)`
                    : generatedFinancingHtml}
                </pre>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(generatedFinancingHtml, "Financing page HTML")}
                  >
                    Copy HTML
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={downloadFinancingHtml}>
                    Download .html
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Prequalification & post-submit</CardTitle>
          <p className="text-sm font-normal text-gray-600">
            When applicants complete and sign the application (e.g. in a modal), choose whether they see prequalified
            offers or only a thank-you message. Prequal runs after sign (credit pull when allowed); these settings control
            what applicants see and whether they receive an upload-documents email.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPrequalsAfterSubmit}
              onChange={(e) => setShowPrequalsAfterSubmit(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">Show prequalified offers to applicants after they sign</span>
          </label>
          <p className="text-xs text-gray-500">
            {showPrequalsAfterSubmit
              ? "Applicants will see prequal results (when available) before closing. Modal stays open until they close it."
              : "Applicants will only see a thank-you message and next steps; prequal results stay internal."}
          </p>
          {canEditRatesDisclosure && (
            <>
              <label className="inline-flex items-center gap-2 cursor-pointer pt-2 border-t border-gray-100">
                <input
                  type="checkbox"
                  checked={showPrequalInterestAndApr}
                  onChange={(e) => setShowPrequalInterestAndApr(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium">Show simple interest rate and APR on prequal offers</span>
              </label>
              <p className="text-xs text-gray-500">
                {showPrequalInterestAndApr
                  ? "Applicants (thank-you) and your team (application detail) see estimated simple interest % and APR alongside payment and term."
                  : "Estimated payment and term still show; simple interest % and APR are hidden. Independent of whether applicants see offers after sign."}
              </p>
            </>
          )}
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={sendUploadLinkEmail}
              onChange={(e) => setSendUploadLinkEmail(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm font-medium">
              Email upload-documents link to customers after prequal selection or manual review
            </span>
          </label>
          <p className="text-xs text-gray-500">
            {sendUploadLinkEmail
              ? "Customers will receive an email with the upload-documents link when they select an offer or request manual review (when an upload link exists)."
              : "Customers will not receive an upload-documents email; they can still use the Upload Documents button/link in the portal."}
          </p>
          <div>
            <Label className="text-sm font-medium">Website URL (redirect after apply)</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-1">
              When prequalified offers are not shown, applicants are redirected to this URL after 10 seconds on the thank-you page. Leave blank to skip redirect.
            </p>
            <Input
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://www.yourcompany.com"
              className="max-w-md"
            />
          </div>
          <div>
            <Label className="text-sm font-medium">Abandon notifications – extra email</Label>
            <p className="text-xs text-gray-500 mt-0.5 mb-1">
              Optional. All abandon notifications (customer started but didn’t complete an application) are also sent to this address (e.g. leads@yourcompany.com). Leave blank to not use.
            </p>
            <Input
              type="email"
              value={abandonNotificationEmail}
              onChange={(e) => setAbandonNotificationEmail(e.target.value)}
              placeholder="leads@yourcompany.com"
              className="max-w-md"
            />
          </div>
          <Button onClick={handleSave} disabled={updateOrg.isPending}>
            {updateOrg.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Industries & Equipment Types</CardTitle>
          <p className="text-sm font-normal text-gray-600">
            Limit which industries and equipment types this organization serves. Leave all unchecked to allow all.
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label className="text-sm font-medium">Industries served</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {industries.length === 0 ? (
                <p className="text-sm text-gray-500">No industries loaded.</p>
              ) : (
                industries.map((ind) => (
                  <label key={ind.id} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedIndustryIds.includes(ind.id)}
                      onChange={() => handleIndustryToggle(ind.id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{ind.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">Equipment types served</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {equipmentTypes.length === 0 ? (
                <p className="text-sm text-gray-500">No equipment types loaded.</p>
              ) : (
                equipmentTypes.map((et) => (
                  <label key={et.id} className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedEquipmentTypeIds.includes(et.id)}
                      onChange={() => handleEquipmentTypeToggle(et.id)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm">{et.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>

          <div className="border-t pt-6">
            <Label className="text-sm font-medium">Primary equipment — extra fields (optional)</Label>
            <p className="text-xs text-gray-500 mt-1 mb-3">
              Up to three short text fields (e.g. unit number) collected on the application form with equipment. All optional for applicants. Max 144 characters each.
            </p>
            <div className="space-y-3 max-w-md">
              {primaryEquipFieldRows.map((row, idx) => (
                <div key={idx}>
                  <Label className="text-xs text-gray-600">Field {idx + 1} label</Label>
                  <Input
                    value={row.label}
                    maxLength={120}
                    placeholder="e.g. Equipment number"
                    onChange={(e) => {
                      const v = e.target.value;
                      setPrimaryEquipFieldRows((prev) =>
                        prev.map((r, i) => (i === idx ? { ...r, label: v } : r))
                      );
                    }}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </div>

          <Button onClick={handleSave} disabled={updateOrg.isPending}>
            {updateOrg.isPending ? "Saving…" : "Save"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Sales reps</CardTitle>
          <p className="text-sm font-normal text-gray-600">
            Invite sales reps to this organization. They will receive an email to set their own password and then can log in and use their application links.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {salesReps.length > 0 && (
            <div>
              <Label className="text-sm font-medium">Current sales reps</Label>
              <ul className="mt-2 space-y-2">
                {salesReps.map((rep) => (
                  <li
                    key={rep.id}
                    className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-700"
                  >
                    <span>
                      {rep.first_name} {rep.last_name} &lt;{rep.email}&gt;{" "}
                      <span
                        className={
                          rep.is_active
                            ? "text-green-700 font-medium"
                            : "text-gray-500 font-medium"
                        }
                      >
                        {rep.is_active ? "Active" : "Inactive"}
                      </span>
                    </span>
                    {canEdit && (
                      rep.is_active ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={deactivateSalesRep.isPending}
                          onClick={() => {
                            const label = `${rep.first_name} ${rep.last_name}`.trim() || rep.email;
                            if (
                              window.confirm(
                                `Deactivate ${label}? They will not be able to log in, but existing deal assignments will be kept.`
                              )
                            ) {
                              deactivateSalesRep.mutate(rep.id);
                            }
                          }}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={reactivateSalesRep.isPending}
                          onClick={() => reactivateSalesRep.mutate(rep.id)}
                        >
                          Reactivate
                        </Button>
                      )
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="space-y-3 pt-2">
            <Label className="text-sm font-medium">Invite a sales rep</Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Input
                placeholder="First name"
                value={inviteFirstName}
                onChange={(e) => setInviteFirstName(e.target.value)}
              />
              <Input
                placeholder="Last name"
                value={inviteLastName}
                onChange={(e) => setInviteLastName(e.target.value)}
              />
              <Input
                type="email"
                placeholder="Email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                const email = inviteEmail.trim().toLowerCase();
                const first_name = inviteFirstName.trim();
                const last_name = inviteLastName.trim();
                if (!email || !first_name || !last_name) {
                  toast.error("Please fill in first name, last name, and email.");
                  return;
                }
                inviteSalesRep.mutate({ email, first_name, last_name });
              }}
              disabled={inviteSalesRep.isPending}
            >
              {inviteSalesRep.isPending ? "Sending…" : "Send invite"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
